import React, { useEffect } from 'react';

interface IUseCloseSERPListeners {
    searchTriggerSelector?: string;
    closeSERP: () => void;
    isOpen: boolean;
    searchElementSelector: string;
    innerSERPRef: React.RefObject<HTMLDivElement>;
}

/**
 * Handles logic of closing the SERP layer, by pressing ESC key or clicking outside of the currently opened SERP.
 * Registers mouse and keyboard event handlers and inside them, perform some checks that prevent closing the SERP if unwanted..
 */
const useCloseSERPListeners = ({
    searchTriggerSelector,
    closeSERP,
    isOpen,
    searchElementSelector,
    innerSERPRef,
}: IUseCloseSERPListeners) => {
    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                closeSERP();
            }
        };

        // in this function we perform a check if any of the native search elements has been clicked. We don't want to
        // closeSERP() when clicking on an input element.
        const handleClickOutside = (event: MouseEvent, showSearch: boolean) => {
            const nativeSearchElements: Element[] = Array.from(document.querySelectorAll(`${searchElementSelector}`));
            if (searchTriggerSelector) {
                const searchTrigger = document.querySelector(`${searchTriggerSelector}`) as Element;
                if (searchTrigger) {
                    nativeSearchElements.push(searchTrigger);
                }
            }

            if (showSearch && innerSERPRef.current && !innerSERPRef.current?.contains(event.target as Node)) {
                if (!event.target) {
                    return;
                }

                const elementsContainedInEventTarget = nativeSearchElements.filter((nativeSearchElement: Element) => {
                    if (nativeSearchElement.contains(event.target as Node)) {
                        return true;
                    }
                    return false;
                });

                if (elementsContainedInEventTarget.length === 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    closeSERP();
                }
            }
        };

        const clickHandler = (event: MouseEvent) => handleClickOutside(event, isOpen);
        const keydownHandler = (event: KeyboardEvent) => handleEsc(event);

        if (isOpen) {
            window.addEventListener('keydown', keydownHandler);
            window.addEventListener('mousedown', clickHandler, true);
        } else {
            window.removeEventListener('keydown', keydownHandler);
            window.removeEventListener('mousedown', clickHandler, true);
        }

        return () => {
            window.removeEventListener('keydown', keydownHandler);
            window.removeEventListener('mousedown', clickHandler, true);
        };
    }, [closeSERP, innerSERPRef, isOpen]);
};

export default useCloseSERPListeners;
